import { DataGrid } from '@mui/x-data-grid'
import { Box, Tooltip, Typography } from '@mui/material'
import TableRowsIcon from '@mui/icons-material/TableRows'
import { useClock } from '../utils/clock'
import { motion } from 'framer-motion'
import Favorite from './Favorite'
import PriceHistoryModal from './PriceHistoryModal'
import theme from '../theme'
import Context from '../Context'
import { buildArbCellIndex, displayBookmaker, isSoftRow } from '../utils/arb'
import { buildValueCellIndex } from '../utils/valueBets'
import { formatDecimalPrice } from '../utils/price'
import { marginOf } from '../utils/margin'
import { useContext, useEffect, useRef, useState } from 'react'

const PRICE_FIELDS = ['homePrice', 'drawPrice', 'awayPrice', 'homeSpreadPrice', 'awaySpreadPrice', 'underPrice', 'overPrice']

// several opportunities on one event get different shades, so the two prices of each stay readable as a pair
const HIGHLIGHT_SHADES = 4

function EventTable({ event, usPrices, showDiscrepancies, showArbs }) {
  const { home, away, koTime, bookieList } = event
  const { setSelectedEvent, settings } = useContext(Context)
  const clock = useClock()
  const arbCells = showArbs ? buildArbCellIndex(event, settings.minArbProfit) : null
  const valueCells = showDiscrepancies ? buildValueCellIndex(event, settings.minYield) : null

  const [ previousPrices, setPreviousPrices ] = useState({})
  const [ highlightedCells, setHighlightedCells ] = useState({})
  const [ historyRequest, setHistoryRequest ] = useState(null)

  const timers = useRef({})

  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout)
  }, [])

  // The diff belongs here, not in renderCell: writing to state during render painted a frame late and,
  // because every move queued its own unkeyed timer, a second move on the same cell was cleared early
  // by the first timer instead of lasting its full three seconds.
  useEffect(() => {
    const currentPrices = {}

    bookieList.forEach(row => {
      // the soft prices are derived from the sharp ones and tick in lockstep with them, so flagging both
      // sides of the same move twice is noise: only the sharp row gets the movement stripe
      if (isSoftRow(row)) {
        return
      }

      const rowId = `${home}|${away}|${koTime}|${row.bookmaker}`
      PRICE_FIELDS.forEach((field) => {
        currentPrices[`${rowId}-${field}`] = row[field]
      })
    })

    const moved = {}
    Object.entries(currentPrices).forEach(([key, price]) => {
      const previous = previousPrices[key]
      if (previous === undefined || price === previous || !(price > 1) || !(previous > 1)) {
        return
      }
      moved[key] = price > previous ? 'price-increase' : 'price-decrease'
    })

    if (Object.keys(moved).length) {
      setHighlightedCells((prev) => ({ ...prev, ...moved }))
      Object.keys(moved).forEach((key) => {
        clearTimeout(timers.current[key])
        timers.current[key] = setTimeout(() => {
          delete timers.current[key]
          setHighlightedCells((prev) => {
            const next = { ...prev }
            delete next[key]
            return next
          })
        }, 3000)
      })
    }

    setPreviousPrices(currentPrices)
    // previousPrices is the thing being compared against, depending on it would loop
    // eslint-disable-next-line
  }, [home, away, koTime, bookieList])

  // must stay in step with RUNNER_KEYS in server/index.js, the history is keyed by exactly this string
  const runnerKeyOf = (row, field) => {
    switch (field) {
      case 'homePrice': return 'ML|HOME'
      case 'drawPrice': return 'ML|DRAW'
      case 'awayPrice': return 'ML|AWAY'
      case 'homeSpreadPrice': return `SPREAD|HOME|${row.spreadPoints}`
      case 'awaySpreadPrice': return `SPREAD|AWAY|${-row.spreadPoints}`
      case 'overPrice': return `TOTAL|OVER|${Number(row.totalPoints)}`
      case 'underPrice': return `TOTAL|UNDER|${Number(row.totalPoints)}`
      default: return null
    }
  }

  const LABELS = {
    homePrice: () => `${home} moneyline`,
    drawPrice: () => 'Draw',
    awayPrice: () => `${away} moneyline`,
    homeSpreadPrice: (row) => `${home} spread ${row.spreadPoints}`,
    awaySpreadPrice: (row) => `${away} spread ${-row.spreadPoints}`,
    overPrice: (row) => `Over ${row.totalPoints}`,
    underPrice: (row) => `Under ${row.totalPoints}`
  }

  // which prices make up the market the clicked one belongs to, so the overround is read off the whole
  // market rather than the single leg
  const ML_LEGS = (row) => [row.homePrice, row.drawPrice, row.awayPrice]
  const SPREAD_LEGS = (row) => [row.homeSpreadPrice, row.awaySpreadPrice]
  const TOTAL_LEGS = (row) => [row.overPrice, row.underPrice]

  const MARKET = {
    homePrice: { name: 'Moneyline', legs: ML_LEGS, selection: () => home },
    drawPrice: { name: 'Moneyline', legs: ML_LEGS, selection: () => 'Draw' },
    awayPrice: { name: 'Moneyline', legs: ML_LEGS, selection: () => away },
    homeSpreadPrice: { name: 'Spread', legs: SPREAD_LEGS, selection: (row) => `${home} ${row.spreadPoints}` },
    awaySpreadPrice: { name: 'Spread', legs: SPREAD_LEGS, selection: (row) => `${away} ${-row.spreadPoints}` },
    overPrice: { name: 'Total', legs: TOTAL_LEGS, selection: (row) => `Over ${row.totalPoints}` },
    underPrice: { name: 'Total', legs: TOTAL_LEGS, selection: (row) => `Under ${row.totalPoints}` }
  }

  const openHistory = (params) => {
    const runnerKey = runnerKeyOf(params.row, params.field)
    if (!runnerKey) {
      return
    }
    const market = MARKET[params.field]
    setHistoryRequest({
      eventKey: `${home}|${away}|${koTime}`,
      home,
      away,
      koTime,
      // the grid only ever shows the whole match, the halves and quarters live on the event screen
      period: 'GAME',
      runnerKey,
      bookmaker: displayBookmaker(params.row.bookmaker),
      price: params.value,
      label: LABELS[params.field](params.row),
      market: market ? market.name : null,
      selection: market ? market.selection(params.row) : LABELS[params.field](params.row),
      margin: market ? marginOf(market.legs(params.row)) : null
    })
  }

  const renderPrice = (params) => {
    // a decimal of exactly 1 is 'no price' in this feed, and -100/(1-1) would render as -Infinity
    if (!(params.value > 1)) {
      return '—'
    }

    return (
      <Box
        onClick={() => openHistory(params)}
        title="Price history"
        sx={{ width: '100%', cursor: 'pointer' }}
      >
        {formatDecimalPrice(params.value, usPrices)}
      </Box>
    )
  }

  const checkDiscrepancies = (params) => {
    if (!valueCells) {
      return ''
    }
    const hit = valueCells.get(`${params.row.bookmaker}|${params.field}`)
    return hit ? `vb-${hit.betIndex % HIGHLIGHT_SHADES}` : ''
  }

  const checkArb = (params) => {
    if (!arbCells) {
      return ''
    }
    const hit = arbCells.get(`${params.row.bookmaker}|${params.field}`)
    return hit ? `hl-${hit.arbIndex % HIGHLIGHT_SHADES}` : ''
  }

  const getCellClass = (params) => {
    const key = `${params.id}-${params.field}`

    // an arb is the stronger statement, so it wins the cell when both apply
    let className = checkArb(params) || checkDiscrepancies(params)

    if (highlightedCells[key] !== undefined) {
      // both classes have to survive: a price can move on a cell that is already an arb or a value bet
      className = `${className} ${highlightedCells[key]}`.trim()
    }

    return className
  }

  // native title, not a MUI Tooltip: the grid repaints on every poll and that resets the hover state
  const renderBookmaker = (params) => (
    <Box
      title={displayBookmaker(params.value)}
      sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {displayBookmaker(params.value)}
    </Box>
  )

  // Three letter headings are only obvious to someone who already knows the screen. Every one of them
  // says in full what it is on hover, naming the actual teams rather than "home" and "away".
  const columns = [
    { field: "bookmaker", headerName: "Bookmaker", description: "The book these prices come from.", flex: 1, disableColumnMenu: true, renderCell: renderBookmaker },
    { field: "homePrice", headerName: "Home", description: `Moneyline price on ${home} to win.`, flex: 1, disableColumnMenu: true, renderCell: renderPrice, cellClassName: getCellClass },
    { field: "drawPrice", headerName: "Draw", description: "Moneyline price on the match ending level.", flex: 1, disableColumnMenu: true, renderCell: renderPrice, cellClassName: getCellClass },
    { field: "awayPrice", headerName: "Away", description: `Moneyline price on ${away} to win.`, flex: 1, disableColumnMenu: true, renderCell: renderPrice, cellClassName: getCellClass },
    { field: "spreadPoints", headerName: "SP", description: `Spread points. The line is quoted for ${home}; ${away} runs the same number with the sign flipped.`, flex: 1, disableColumnMenu: true, renderCell: (params) => params.value === 0 && params.row.homeSpreadPrice === 0 ? '—' : params.value },
    { field: "homeSpreadPrice", headerName: "SPH", description: `Spread price on ${home}, at the line in the SP column.`, flex: 1, disableColumnMenu: true, renderCell: renderPrice, cellClassName: getCellClass },
    { field: "awaySpreadPrice", headerName: "SPA", description: `Spread price on ${away}, at the mirrored line.`, flex: 1, disableColumnMenu: true, renderCell: renderPrice, cellClassName: getCellClass },
    { field: "totalPoints", headerName: "Total", description: "The total line: how many goals or points the book expects in the match.", flex: 1, disableColumnMenu: true, renderCell: (params) => params.value === 0 ? '—' : params.value },
    { field: "underPrice", headerName: "Under", description: "Price on the match finishing below the total.", flex: 1, disableColumnMenu: true, renderCell: renderPrice, cellClassName: getCellClass },
    { field: "overPrice", headerName: "Over", description: "Price on the match finishing above the total.", flex: 1, disableColumnMenu: true, renderCell: renderPrice, cellClassName: getCellClass }
  ]

  // the clock the whole app is set to, rather than this machine's own
  const formattedDate = (koTime) => clock.kickOff(koTime)

  return(
    <Box>
      {/* on a narrow panel a long pair of names plus the link is wider than the row: let the link drop
          to its own line rather than be cut off or push a scrollbar under the whole table */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: '4px',
        position: 'relative'
      }}>
        {/* the plate holds its own width: the link to its right must not be able to squeeze the team
            names onto a second line and drag the diagonal out of place with them */}
        <Box
          sx={{
            backgroundColor: theme.palette.primary.light,
            padding: '3px',
            position: 'relative',
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0
          }}
        >
          <Favorite event={event} />
          <Box
            title="Show every line for this event"
            onClick={() => setSelectedEvent({ home, away, koTime })}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textDecoration: 'underline' }}>{home}</Typography>
            <Typography variant="h6" sx={{ color: 'white' }}>&nbsp;vs&nbsp;</Typography>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textDecoration: 'underline', paddingRight: '10px' }}>{away}</Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: '-10px',
              width: 0,
              height: 0,
              borderTop: '0px solid transparent',
              borderBottom: '45px solid transparent',
              borderRight: '0px solid #07B082',
              borderLeft: '10px solid #07B082',
            }}
          />
        </Box>
        <Box
          sx={{
            padding: '10px',
            marginLeft: '10px',
            flexShrink: 0
          }}
        >
          <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>{formattedDate(koTime)}</Typography>
        </Box>
        {/* the team plate opens the same screen, but nothing about it says so. An icon on its own was
            the same problem one size smaller, so it says in words that it is a way through */}
        <Tooltip title="Every line this event has: all periods, all markets, from each book" placement="top">
          <Box
            onClick={() => setSelectedEvent({ home, away, koTime })}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              marginLeft: '4px',
              cursor: 'pointer',
              color: theme.palette.primary.main,
              '&:hover': { color: theme.palette.primary.light }
            }}
          >
            <TableRowsIcon fontSize="small" />
            <Typography variant="h6" sx={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>
              Show all lines
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        key={usPrices}
        // the grid measures its parent on mount and this div has no intrinsic width of its own,
        // so without this it comes up 0px wide and renders nothing until something forces a reflow
        style={{ width: '100%' }}
      >
        <DataGrid
          rows={bookieList}
          columns={columns}
          getRowId={(row) => `${home}|${away}|${koTime}|${row.bookmaker}`}
          rowHeight={30}
          autoHeight
          hideFooter
          sx={{
            borderRadius: 0,
            backgroundColor: 'white',
            border: 'none',
            // one shade per opportunity, so two pairs on the same event do not read as one.
            // arbs and value bets keep separate palettes, otherwise an unrelated value cell can land on
            // the same shade as an arb pair and read as part of it
            '& .hl-0': { backgroundColor: '#E57373', fontWeight: 'bold' },
            '& .hl-1': { backgroundColor: '#EF9A9A', fontWeight: 'bold' },
            '& .hl-2': { backgroundColor: '#F4B9B9', fontWeight: 'bold' },
            '& .hl-3': { backgroundColor: '#FBD5D5', fontWeight: 'bold' },
            '& .vb-0': { backgroundColor: '#F6C177', fontWeight: 'bold' },
            '& .vb-1': { backgroundColor: '#F9D49B', fontWeight: 'bold' },
            '& .vb-2': { backgroundColor: '#FBE3BF', fontWeight: 'bold' },
            '& .vb-3': { backgroundColor: '#FDF0DC', fontWeight: 'bold' },
            '& .price-increase::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: '10%',
              bottom: 0,
              left: 0,
              width: '3px',
              height: '80%',
              backgroundColor: theme.palette.primary.light,
            },
            '& .price-decrease::after': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: '10%',
              bottom: 0,
              left: 0,
              width: '3px',
              height: '80%',
              backgroundColor: theme.palette.secondary.main,
            },
            '& .MuiDataGrid-cell': {
              position: 'relative',
            },
          }}
        />
      </motion.div>
      <PriceHistoryModal request={historyRequest} onClose={() => setHistoryRequest(null)} />
    </Box>
  )
}

export default EventTable